import {type TagDescription} from '@reduxjs/toolkit/query';

import {isDefined} from 'shared/web';

type InvalidatedTags<TTagType extends string> = ReadonlyArray<
  TagDescription<TTagType> | null | undefined
>;

/**
 * Creates an invalidatesTags function that only invalidates cache on successful requests.
 *
 * @param getTags - Function or array that defines which tags to invalidate on success
 * @returns RTK Query invalidatesTags function that conditionally invalidates
 *
 * @example Simple array of tags
 * ```typescript
 * invalidatesTags: invalidateOnSuccess(['receiveNote', 'deliveryNote'])
 * ```
 *
 * @example Dynamic tags based on query arguments
 * ```typescript
 * invalidatesTags: invalidateOnSuccess((_result, queryArg: PatchReceiveNoteApiArg) => [
 *   {type: 'receiveNoteBasket', id: queryArg.receiveNoteId},
 *   {type: 'receiveNote', id: queryArg.receiveNoteId},
 * ])
 * ```
 *
 * @remarks
 * Tag names are checked against the `tagTypes` union of the api instance the endpoint is
 * injected into, so a misspelled tag is a compile error at the `invalidatesTags` assignment.
 * The check relies on `TTagType` being inferred from the tag literals. Two things defeat that
 * inference and widen `TTagType` to `string`, which then fails to assign:
 * - passing explicit type arguments (annotate the callback parameters inline instead);
 * - reading the tags from a shared array or helper whose element `type` widened to `string`
 *   (add `as const` to that array literal).
 */
export const invalidateOnSuccess =
  <TResult = unknown, TQueryArg = unknown, TTagType extends string = string>(
    getTags:
      | InvalidatedTags<TTagType>
      | ((result: TResult | undefined, queryArg: TQueryArg) => InvalidatedTags<TTagType>)
  ) =>
  (result: TResult | undefined, error: unknown, queryArg: TQueryArg): InvalidatedTags<TTagType> => {
    // Only invalidate if there's no error
    if (isDefined(error)) {
      return [];
    }

    // Support both static arrays and dynamic functions
    if (typeof getTags === 'function') {
      return getTags(result, queryArg);
    }

    return getTags;
  };
